import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import WSTextField, { CurrencyType, TextType } from "../../../Components/TextBox/WSTextBox";
import WSDateField from "../../../Components/DateField/WSDateField";
import { callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import dayjs from "dayjs";
import { HeaderList, IHeaderListActions } from "../../../Components/Header/HeaderList";
import { WSIcons } from "../../../commonFunctions/iIcons";

const SanctionLoan: React.FC<any> = React.memo((props) => {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loanStatus, setLoanStatus] = useState<string | null>(null);
    const { showMessage } = useSnackbar();
    const { handleSubmit, control, reset, setValue } = useForm();

    const navigate = useNavigate();
    const params = useParams();
    const id = params?.loanId;

    useEffect(() => {
        const fetchDataById = async () => {
            setLoading(true);
            try {
                const response: any = await callAPI(
                    "loans/getBy",
                    Method.GET,
                    {},
                    id
                );

                if (response.success) {
                    const loan = response.loan;
                    

                    setLoanStatus(loan.status)
                    setValue("sanctionedAmount", loan.appliedAmount || 0)
                    setValue("appliedAmount", loan.appliedAmount || 0)
                    setValue(
                        "sanctionedDate",
                        loan.sanctionedDate
                            ? dayjs(loan.sanctionedDate).format("MM/DD/YYYY")
                            : dayjs().format("MM/DD/YYYY")
                    );

                    setValue("duration", 24);
                    // setValue("principal", loan.principal || "");
                    setValue("interestRate", loan.interestRate || 0);

                }

            } catch (error: any) {
                console.error("Failed to retrieve data", error);
                if (error.message === 'GlobalHandledError') return;
                showMessage(
                    error?.response?.data?.message || "Failed to retrieve data",
                    "error"
                );
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchDataById();
    }, [id]);

    const handleCancelClick = () => {
        navigate("/loan/requests/list");
    };


    const handleSanctionLoan = async (data: any) => {

        try {
            setSubmitting(true);
            const request = {
                sanctionedCefAmount: data.sanctionedAmount,
                sanctionedDate: data.sanctionedDate,
                interestRate: data.interestRate,
                duration: data.duration || 24,
                // principal: data.principal,
            };

            const response: any = await callAPI(`loans/sanctionLoan/update/${id}`, Method.PATCH, request);
            if (response.success) {
                showMessage(response.message, "success");
                navigate("/loan/requests/final/list");
                reset();
            }
            
        } catch (error: any) {
            if (error.message === 'GlobalHandledError') return;
            showMessage(
                error.response?.data.message || "An unexpected error occurred",
                "error"
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (loading)
        return (
            <Box height="80vh" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress sx={{ color: "#4AD06E" }} />
            </Box>
        );

    const actions: IHeaderListActions[] = [
        {
            type: "button",
            text: "Cancel",
            size: "small",
            onClick: handleCancelClick,
            variant: "outlined",
            color: "error",
            icon: <WSIcons.CloseIcon />,
            disabled: submitting,
        },
        {
            type: "button",
            text: "Sanction",
            size: "small",
            buttonType: "submit",
            variant: "outlined",
            color: "success",
            icon: <WSIcons.Save />,
            loading: submitting,
        }
    ];

    const renderFormContent = () => {
        if (loanStatus === 'pending') {
            return (
                <form onSubmit={handleSubmit(handleSanctionLoan)}>
                    <HeaderList title="Time to sanction the Loan" type="simple" actions={actions} headerSx={{ color: "#022324" }} isBackButton />
                    <Grid
                        item
                        container
                        spacing={1}
                        justifyContent={"start"}
                        sx={{
                            background: `linear-gradient(180deg, #4AD16E 0%, #08B1B6 100%)`,
                            color: "#fff",
                            marginTop: 5,
                            marginLeft: 0,
                            width: "100%",
                            backgroundColor: "#91c552",
                            borderTopLeftRadius: 5,
                            borderTopRightRadius: 5
                        }}
                    >
                        <Grid
                            alignContent={"center"}
                            style={{ paddingLeft: 20, paddingTop: 0, height: "3rem" }}
                            item
                            sm={4}
                            className="ws-data-grid-lightviolet-title"
                        >
                            <Typography variant="h6" id="tableTitle" sx={{ fontSize: "1rem" }}>
                                Loan Information
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Form Body */}
                    <Grid
                        sx={{
                            padding: 3,
                            backgroundColor: "#fff",
                            borderBottomLeftRadius: 5,
                            borderBottomRightRadius: 5,
                            border: "1px solid lightgrey"
                        }}
                    >
                        <Box
                            sx={{
                                position: 'relative',
                            }}
                        >
                            <Grid container spacing={2}>

                                <Grid item xs={12} sm={6}>
                                    <WSTextField
                                        id="appliedAmount"
                                        control={control}
                                        name="appliedAmount"
                                        label="Applied Amount"
                                        type={TextType.Currency}
                                        currency={CurrencyType.Rupee}
                                        disabled

                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <WSTextField
                                        id="sanctionedAmount"
                                        control={control}
                                        name="sanctionedAmount"
                                        label="Sanctioned Amount"
                                        type={TextType.Currency}
                                        currency={CurrencyType.Rupee}
                                        rules={{ required: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <WSDateField
                                        id="sanctionedDate"
                                        name="sanctionedDate"
                                        label="Sanctioned Date"
                                        control={control}
                                        rules={{ required: true }}
                                    />
                                </Grid>

                                {/* <Grid item xs={12} sm={6}>
                                    <WSTextField
                                        id="principal"
                                        control={control}
                                        name="principal"
                                        label="Principal Amount"
                                        type={TextType.Currency}
                                        currency={CurrencyType.Rupee}
                                        rules={{ required: true }}

                                    />
                                </Grid> */}

                                <Grid item xs={12} sm={6}>
                                    <WSTextField
                                        id="interestRate"
                                        control={control}
                                        name="interestRate"
                                        label="Interest Rate"
                                        type={TextType.Number}
                                        rules={{ required: true }}

                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <WSTextField
                                        id="duration"
                                        control={control}
                                        name="duration"
                                        label="Repayment Months (Durations)"
                                        type={TextType.AllowOnlyNumber}
                                        rules={{ required: true }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </form>
            );
        } else {
            return (
                <><HeaderList title="Time to finalize the Loan" type="simple" actions={actions} />
                    <Grid
                        item
                        container
                        spacing={1}
                        justifyContent={"start"}
                        sx={{
                            marginTop: 5,
                            marginLeft: 0,
                            width: "100%",
                            backgroundColor: "#91c552",
                            borderTopLeftRadius: 5,
                            borderTopRightRadius: 5
                        }}
                    >
                        <Grid
                            alignContent={"center"}
                            style={{ paddingLeft: 20, paddingTop: 0, height: "3rem" }}
                            item
                            sm={4}
                            className="ws-data-grid-lightviolet-title"
                        >
                            <Typography variant="h6" id="tableTitle" sx={{ fontSize: "1rem" }}>
                                Loan Information
                            </Typography>
                        </Grid>
                    </Grid>

                    <Grid sx={{
                        padding: 3,
                        backgroundColor: "#fff",
                        borderBottomLeftRadius: 5,
                        borderBottomRightRadius: 5,
                        border: "1px solid lightgrey"
                    }}>
                        <Grid container spacing={2} mt={1}><Grid item xs={12}>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                D. Fund Disbursement Details
                            </Typography>
                        </Grid>

                            <Grid item xs={12} sm={6}>
                                <WSDateField
                                    id="disbursementDate"
                                    name="disbursementDate"
                                    label="Date of Fund Disbursement"
                                    control={control}
                                    rules={{ required: true }}
                                    defaultValue={new Date()}
                                />
                            </Grid>
                        </Grid>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backdropFilter: 'blur(2px)',
                                backgroundColor: 'rgba(255,255,255,0.3)',
                                zIndex: 1,
                                borderRadius: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: 2,
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 2, color: 'black' }}>
                                Only Pending Loans Can be Sanction
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => window.history.back()}
                                sx={{ zIndex: 2 }}
                            >
                                Back
                            </Button>
                        </Box>
                    </Grid>
                </>)
        }
    };

    return <>{renderFormContent()}</>;
});

export default SanctionLoan;